import React, { useMemo } from 'react';

const formatPhone = (digits) => {
  if (!digits) return '';
  const trimmed = digits.slice(0, 11);
  if (trimmed.length <= 2) {
    return `(${trimmed}`;
  }
  if (trimmed.length <= 6) {
    return `(${trimmed.slice(0, 2)}) ${trimmed.slice(2)}`;
  }
  if (trimmed.length <= 10) {
    return `(${trimmed.slice(0, 2)}) ${trimmed.slice(2, 6)}-${trimmed.slice(6)}`;
  }
  return `(${trimmed.slice(0, 2)}) ${trimmed.slice(2, 7)}-${trimmed.slice(7, 11)}`;
};

const formatCpf = (digits) => {
  if (!digits) return '';
  const trimmed = digits.slice(0, 11);
  const parts = [
    trimmed.slice(0, 3),
    trimmed.slice(3, 6),
    trimmed.slice(6, 9),
    trimmed.slice(9, 11),
  ];
  if (trimmed.length <= 3) return parts[0];
  if (trimmed.length <= 6) return `${parts[0]}.${parts[1]}`;
  if (trimmed.length <= 9) return `${parts[0]}.${parts[1]}.${parts[2]}`;
  return `${parts[0]}.${parts[1]}.${parts[2]}-${parts[3]}`;
};

const formatCnpj = (digits) => {
  if (!digits) return '';
  const trimmed = digits.slice(0, 14);
  const parts = [
    trimmed.slice(0, 2),
    trimmed.slice(2, 5),
    trimmed.slice(5, 8),
    trimmed.slice(8, 12),
    trimmed.slice(12, 14),
  ];
  if (trimmed.length <= 2) return parts[0];
  if (trimmed.length <= 5) return `${parts[0]}.${parts[1]}`;
  if (trimmed.length <= 8) return `${parts[0]}.${parts[1]}.${parts[2]}`;
  if (trimmed.length <= 12) {
    return `${parts[0]}.${parts[1]}.${parts[2]}/${parts[3]}`;
  }
  return `${parts[0]}.${parts[1]}.${parts[2]}/${parts[3]}-${parts[4]}`;
};

const formatCep = (digits) => {
  if (!digits) return '';
  const trimmed = digits.slice(0, 8);
  if (trimmed.length <= 5) return trimmed;
  return `${trimmed.slice(0, 5)}-${trimmed.slice(5, 8)}`;
};

const formatCurrency = (digits) => {
  if (!digits) return '';
  const padded = digits.replace(/^0+/, '').padStart(3, '0');
  const cents = padded.slice(-2);
  const integerPart = padded.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${integerPart},${cents}`;
};

const formatters = {
  phone: formatPhone,
  cpf: formatCpf,
  cnpj: formatCnpj,
  cep: formatCep,
  currency: formatCurrency,
};

const rawExtractors = {
  phone: (value) => value.replace(/\D/g, '').slice(0, 11),
  cpf: (value) => value.replace(/\D/g, '').slice(0, 11),
  cnpj: (value) => value.replace(/\D/g, '').slice(0, 14),
  cep: (value) => value.replace(/\D/g, '').slice(0, 8),
  currency: (value) => value.replace(/\D/g, '').slice(0, 14),
};

const inputMetadata = {
  phone: {
    inputMode: 'numeric',
    pattern: '\\(\\d{2}\\) \\d{4,5}-\\d{4}',
    placeholder: '(11) 91234-5678',
  },
  cpf: {
    inputMode: 'numeric',
    pattern: '\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}',
    placeholder: '123.456.789-00',
  },
  cnpj: {
    inputMode: 'numeric',
    pattern: '\\d{2}\\.\\d{3}\\.\\d{3}/\\d{4}-\\d{2}',
    placeholder: '12.345.678/0001-90',
  },
  cep: {
    inputMode: 'numeric',
    pattern: '\\d{5}-\\d{3}',
    placeholder: '12345-678',
  },
  currency: {
    inputMode: 'numeric',
    placeholder: 'R$ 0,00',
  },
};

export default function MaskedInput({
  mask,
  value,
  onChange,
  onBlur,
  readOnly = false,
  placeholder,
  ...props
}) {
  const formatter = useMemo(() => formatters[mask] || ((val) => val), [mask]);
  const extractor = useMemo(() => rawExtractors[mask] || ((val) => val), [mask]);
  const metadata = inputMetadata[mask] || {};
  const displayValue = useMemo(() => {
    if (value == null) return '';
    const stringValue = typeof value === 'number' ? String(value) : String(value);
    const raw = extractor(stringValue);
    return formatter(raw);
  }, [value, extractor, formatter]);

  const cloneTarget = (target, value, rawValue) => {
    const cloned = {
      name: target.name,
      id: target.id,
      type: target.type,
      value,
      rawValue,
    };

    if (target.dataset) {
      cloned.dataset = target.dataset;
    }

    if ('checked' in target) {
      cloned.checked = target.checked;
    }

    return cloned;
  };

  const handleChange = (event) => {
    const inputValue = event.target.value || '';
    const rawValue = extractor(inputValue);
    const formattedValue = formatter(rawValue);
    if (onChange) {
      onChange({
        ...event,
        target: cloneTarget(event.target, formattedValue, rawValue),
      });
    }
  };

  const handleBlur = (event) => {
    if (onBlur) {
      const rawValue = extractor(event.target.value || '');
      const formattedValue = formatter(rawValue);
      onBlur({
        ...event,
        target: cloneTarget(event.target, formattedValue, rawValue),
      });
    }
  };

  const metadataProps = { ...metadata };
  delete metadataProps.placeholder;

  const effectivePlaceholder =
    placeholder !== undefined ? placeholder : metadata.placeholder;

  if (!readOnly && effectivePlaceholder !== undefined) {
    metadataProps.placeholder = effectivePlaceholder;
  }

  return (
    <input
      {...metadataProps}
      {...props}
      readOnly={readOnly}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
